import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'

import Table from 'components/Table'

import WistiaVideoRow from 'pages/shared/WistiaVideoRow'
import EmptyStatePanel from 'pages/shared/EmptyStatePanel'

import { screenSize, TabPanelTitle } from 'styles/shared'

import Resource from 'types/Resource'
import Spinner from 'components/Spinner'

import { DEFAULT_NO_RESULTS_MESSAGE } from 'helpers/constants'

type Props = {
  searchedValue: string
  videos: Array<Resource>
  isLoading: boolean
}

const FieldTechHelp = ({ searchedValue, videos, isLoading }: Props) => {
  const [filteredVideos, setFilteredVideos] = useState<Array<Resource>>(videos)

  const [sortDirections, setSortDirections] = useState<{
    name: 'asc' | 'desc' | null
    updatedDate: 'asc' | 'desc' | null
    createdDate: 'asc' | 'desc' | null
  }>({
    name: null,
    updatedDate: null,
    createdDate: null
  })

  const handleSort = useCallback(
    (columnType: 'name' | 'updatedDate' | 'createdDate') => {
      const getNextSortDirection = (
        currentDirection: 'asc' | 'desc' | null
      ): 'asc' | 'desc' =>
        currentDirection === null
          ? 'desc'
          : currentDirection === 'desc'
            ? 'asc'
            : 'desc'

      const nextDirection = getNextSortDirection(sortDirections[columnType])

      setSortDirections({
        name: null,
        updatedDate: null,
        createdDate: null,
        [columnType]: nextDirection
      })

      setFilteredVideos((previousVideos) => {
        if (!previousVideos.length) return previousVideos
        return [...previousVideos].sort((videoA, videoB) => {
          let comparisonResult: number

          switch (columnType) {
            case 'name': {
              comparisonResult = videoA.name.localeCompare(videoB.name)
              break
            }
            case 'updatedDate': {
              const videoAUpdatedTime = new Date(
                videoA.wistiaUpdatedAt || videoA.updatedAt || 0
              ).getTime()
              const videoBUpdatedTime = new Date(
                videoB.wistiaUpdatedAt || videoB.updatedAt || 0
              ).getTime()
              comparisonResult = videoAUpdatedTime - videoBUpdatedTime
              break
            }
            case 'createdDate': {
              const videoACreatedTime = new Date(
                videoA.wistiaCreatedAt || videoA.createdAt || 0
              ).getTime()
              const videoBCreatedTime = new Date(
                videoB.wistiaCreatedAt || videoB.createdAt || 0
              ).getTime()
              comparisonResult = videoACreatedTime - videoBCreatedTime
              break
            }
            default:
              comparisonResult = 0
          }

          return nextDirection === 'asc' ? comparisonResult : -comparisonResult
        })
      })
    },
    [sortDirections]
  )

  useEffect(() => {
    setFilteredVideos(videos)
  }, [videos])

  if (isLoading) {
    return (
      <Spinner variant="page" />
    )
  }

  if (!searchedValue || filteredVideos.length === 0) {
    return (
      <EmptyStatePanel
        headingText="Field Tech Help"
        descriptionText="Please enter a search term to find Field Tech Help videos"
        canShowNoResultsBanner={!!searchedValue && filteredVideos.length === 0}
        noResultsHeadingText={DEFAULT_NO_RESULTS_MESSAGE.noResultsHeadingText}
        noResultsDescriptionText={DEFAULT_NO_RESULTS_MESSAGE.noResultsDescriptionText}
      />
    )
  }

  return (
    <>
      <TabPanelTitle>Field Tech Help</TabPanelTitle>
      <>
        <S.TableHeader>
          <S.HeaderCell title="Name" sortable sortDirection={sortDirections.name} onSort={() => handleSort('name')} />
          <S.DescriptionHeaderCell title="Description" />
          <S.HeaderCell title="Updated" sortable sortDirection={sortDirections.updatedDate} onSort={() => handleSort('updatedDate')} />
          <S.HeaderCell title="Created"sortable sortDirection={sortDirections.createdDate} onSort={() => handleSort('createdDate')} />
        </S.TableHeader>
        { filteredVideos.map((video) => (
          <S.CardContainer key={video.hashedId}>
            <WistiaVideoRow video={video} key={video.hashedId} canShowCreationDate />
          </S.CardContainer>
        ))}
      </>
    </>
  )
}

export default FieldTechHelp

const S = {
  CardContainer: styled.div`
    margin: 20px;
    height: 100%;

    @media ${screenSize.medium} {
      margin: 5px;
    }
  `,
  HeaderCell: styled(Table.HeaderCell)`
    flex-basis: 25%;
  `,
  DescriptionHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 50%;
  `,
  TableHeader: styled(Table.Header)`
    margin: 10px;

    @media ${screenSize.medium} {
      font-size: 12px;
    }
  `
}
